/**
 * TRITAL Care® — Live Capability Statement Builder
 *
 * Procurement/supplier-diversity teams personalize a single-page capability
 * document, preview it live, share it via a stateless URL, print to PDF,
 * or email it to procurement. Every shareable URL carries a QR-verifiable
 * snapshot of the configuration → recipients scan and land on /verify which
 * confirms the document's timestamp + integrity hash.
 *
 * No backend writes. Fragment-only encoding. 100% client-side.
 */

import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import { toast } from "sonner";
import {
  Sparkles, Printer, Link2, Mail, Copy, RefreshCw, ShieldCheck,
  CheckCircle2, FileText, Building2, ArrowLeft, Eye, QrCode, Download,
} from "lucide-react";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import PageMeta from "../../components/seo/PageMeta";
import CapabilityDocument, { NAICS_CATALOG, VEHICLE_CATALOG } from "./CapabilityDocument";
import { encodeCapability, decodeCapability, DEFAULT_CAPABILITY } from "../../utils/capabilityLink";

export default function CapabilityStatement() {
  const location = useLocation();
  const navigate = useNavigate();

  // Prefill from URL fragment if present
  const prefilled = useMemo(() => {
    if (!location.hash) return null;
    const r = decodeCapability(location.hash);
    return r.ok ? r.payload : null;
  }, [location.hash]);

  const [recipient, setRecipient] = useState(prefilled?.recipient || DEFAULT_CAPABILITY.recipient);
  const [vehicles, setVehicles] = useState(prefilled?.vehicles || DEFAULT_CAPABILITY.vehicles);
  const [naics, setNaics] = useState(prefilled?.naics || DEFAULT_CAPABILITY.naics);
  const [note, setNote] = useState(prefilled?.note || DEFAULT_CAPABILITY.note);

  const payload = useMemo(() => ({ recipient, vehicles, naics, note }), [recipient, vehicles, naics, note]);

  // Encode — stable against the payload
  const encoded = useMemo(() => encodeCapability(payload), [payload]);

  // QR code → data URL (regenerate whenever verify URL changes)
  const [qrDataUrl, setQrDataUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(encoded.verifyUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 320,
      color: { dark: "#0A1F3F", light: "#FFFFFF" },
    })
      .then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(null); });
    return () => { cancelled = true; };
  }, [encoded.verifyUrl]);

  // Mirror the encoded config into the URL hash (silent, replace-state) so that
  // refreshing the page preserves personalization and the displayed "share link"
  // tallies byte-for-byte with what will be stamped on the PDF.
  useEffect(() => {
    const hash = encoded.configUrl.split("#")[1];
    if (hash && hash !== location.hash.slice(1)) {
      window.history.replaceState(null, "", `#${hash}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encoded.configUrl]);

  const toggle = useCallback((arr, setter) => (val) => {
    setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(encoded.configUrl).then(
      () => toast.success("Shareable link copied", { description: "90-day validity · QR stays in sync." }),
      () => toast.error("Copy failed — long-press the link field to copy manually."),
    );
  };

  const emailStatement = () => {
    const subject = encodeURIComponent(`TRITAL Care — Capability Statement${recipient.org ? ` for ${recipient.org}` : ""}`);
    const bodyLines = [
      recipient.contact ? `Hi ${recipient.contact.split(" ")[0]},` : "Hello,",
      "",
      `Please find below the TRITAL Care capability statement${recipient.org ? ` prepared for ${recipient.org}` : ""}.`,
      "",
      "Open the live, personalized document:",
      encoded.configUrl,
      "",
      "The recipient can scan the QR code on the document (or follow the verify link) to confirm certification status in real-time.",
      "",
      "Best regards,",
      "TRITAL Care — Supplier Diversity",
      "partners@tritalcare.com · +1 (832) 303-6622",
    ];
    const body = encodeURIComponent(bodyLines.join("\n"));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const printDoc = () => window.print();

  const reset = () => {
    if (!window.confirm("Reset personalization to defaults?")) return;
    setRecipient(DEFAULT_CAPABILITY.recipient);
    setVehicles(DEFAULT_CAPABILITY.vehicles);
    setNaics(DEFAULT_CAPABILITY.naics);
    setNote("");
    navigate("/capability-statement", { replace: true });
    toast.info("Reset to defaults");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="capability-statement-page">
      <PageMeta
        title="Capability Statement Builder | TRITAL Care®"
        description="Generate a personalized, procurement-ready one-page capability statement for TRITAL Care® — with live QR verification, NAICS codes, contract vehicles, and shareable links. TRITAL Care is NMSDC/HMSDC MBE-certified, NCTRCA dual MBE/SBE, and SAM.gov-registered."
        canonical="https://www.tritalcare.com/capability-statement"
      />
      <div className="no-print"><PublicNav /></div>

      {/* Hero */}
      <section className="no-print relative overflow-hidden bg-trital-mesh border-b border-slate-200" data-testid="capability-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-10 lg:py-14">
          <Link to="/diversity-and-inclusion" className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-trital-navy/80 hover:text-trital-navy" data-testid="capability-back">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back to Diversity &amp; Inclusion
          </Link>
          <div className="mt-4 grid lg:grid-cols-12 gap-6 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
                <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
                <span className="overline text-trital-navy">Procurement-ready · single page</span>
              </div>
              <h1 className="mt-4 font-display font-black text-4xl lg:text-[52px] leading-[1.04] tracking-tight text-slate-900 text-balance">
                Build your <span className="text-trital-navy">capability statement</span>,
                <span className="block text-trital-red">QR-verified &amp; print-ready.</span>
              </h1>
              <p className="mt-4 max-w-2xl text-slate-600 leading-relaxed">
                Personalize this live one-pager for a specific hospital, pick the NAICS codes and contract vehicles that matter,
                then print to PDF or send a shareable link. Every document carries a unique QR code — scan it and the recipient lands
                on a verification page that confirms this configuration was issued by TRITAL Care, with a timestamp and integrity hash.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-card">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-trital-red">
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} /> What's new
                </div>
                <ul className="mt-3 space-y-1.5 text-[12.5px] text-slate-700">
                  <li className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} /> Live personalization — hospital name, NAICS, contract vehicles</li>
                  <li className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} /> Shareable URL that opens the same personalized doc</li>
                  <li className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} /> QR code embedded in the PDF for scan-to-verify</li>
                  <li className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2} /> No backend, no account, zero data retention</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Action bar (sticky) */}
      <div className="no-print sticky top-[84px] z-20 bg-white/85 backdrop-blur border-b border-slate-200" data-testid="capability-actions">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-3 flex flex-wrap items-center gap-2">
          <button onClick={printDoc} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold btn-lift hover:bg-trital-navy-deep" data-testid="capability-print">
            <Printer className="w-4 h-4" strokeWidth={1.8} /> Print / Save as PDF
          </button>
          <button onClick={copyLink} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-trital-navy text-[13px] font-semibold hover:bg-slate-50" data-testid="capability-copy">
            <Link2 className="w-4 h-4" strokeWidth={1.8} /> Copy shareable link
          </button>
          <button onClick={emailStatement} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-trital-navy text-[13px] font-semibold hover:bg-slate-50" data-testid="capability-email">
            <Mail className="w-4 h-4" strokeWidth={1.8} /> Email to procurement
          </button>
          <button onClick={reset} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-slate-500 text-[12px] font-semibold hover:text-slate-900" data-testid="capability-reset">
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} /> Reset
          </button>
        </div>
      </div>

      {/* Main grid: form + live preview */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-8 lg:py-12 grid lg:grid-cols-[380px_1fr] gap-8 items-start" data-testid="capability-grid">
        {/* Personalization form */}
        <aside className="no-print space-y-5 lg:sticky lg:top-[148px]" data-testid="capability-form">
          <Field label="Prepared for (hospital / system)" testId="field-recipient-org">
            <input
              type="text"
              value={recipient.org}
              onChange={(e) => setRecipient({ ...recipient, org: e.target.value })}
              placeholder="Houston Methodist · HCA Gulf Coast · Memorial Hermann"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
              data-testid="input-recipient-org"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Recipient name">
              <input
                type="text"
                value={recipient.contact}
                onChange={(e) => setRecipient({ ...recipient, contact: e.target.value })}
                placeholder="Jane Doe"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
                data-testid="input-recipient-contact"
              />
            </Field>
            <Field label="Title">
              <input
                type="text"
                value={recipient.title}
                onChange={(e) => setRecipient({ ...recipient, title: e.target.value })}
                placeholder="Supplier Diversity Mgr"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
                data-testid="input-recipient-title"
              />
            </Field>
          </div>

          <Field label="Contract vehicles" testId="field-vehicles">
            <div className="flex flex-wrap gap-1.5">
              {VEHICLE_CATALOG.map((v) => {
                const active = vehicles.includes(v.key);
                return (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => toggle(vehicles, setVehicles)(v.key)}
                    className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-full border transition ${
                      active
                        ? "bg-trital-navy text-white border-trital-navy"
                        : "bg-white text-slate-700 border-slate-300 hover:border-trital-navy/60"
                    }`}
                    data-testid={`vehicle-${v.key}`}
                    aria-pressed={active}
                  >
                    {v.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label="NAICS codes" testId="field-naics">
            <div className="grid grid-cols-1 gap-1">
              {NAICS_CATALOG.map((n) => {
                const active = naics.includes(n.code);
                return (
                  <label key={n.code} className={`flex items-start gap-2 px-2.5 py-1.5 rounded-lg border text-[12px] cursor-pointer transition ${active ? "border-trital-navy bg-trital-medical-blue-soft" : "border-slate-200 bg-white hover:border-slate-300"}`} data-testid={`naics-${n.code}`}>
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggle(naics, setNaics)(n.code)}
                      className="mt-0.5 accent-trital-navy"
                    />
                    <div className="min-w-0">
                      <span className="font-mono font-bold text-trital-navy">{n.code}</span>{" "}
                      <span className="text-slate-700">{n.label}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </Field>

          <Field label="Note to procurement (optional)" testId="field-note">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 320))}
              rows={3}
              placeholder="e.g. Following up on your Q2 supplier-diversity RFI — please find our capability snapshot below."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30 resize-none"
              data-testid="input-note"
            />
            <div className="mt-1 text-[11px] text-slate-400 text-right">{note.length}/320</div>
          </Field>

          {/* Share link status */}
          <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="share-link-card">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-trital-red">
              <QrCode className="w-3.5 h-3.5" strokeWidth={2} /> Live share link
            </div>
            <div className="mt-2 flex items-start gap-2">
              <input
                readOnly
                value={encoded.configUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 min-w-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-[11px] font-mono text-slate-700"
                data-testid="share-link-input"
              />
              <button
                onClick={copyLink}
                className="p-2 rounded-md border border-slate-200 text-trital-navy hover:bg-slate-50"
                title="Copy link"
                data-testid="share-link-copy"
              >
                <Copy className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between text-[10.5px] text-slate-500">
              <span>Expires {new Date(encoded.expiresAt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>
              <span className="font-mono">{encoded.sizeBytes} bytes</span>
            </div>
          </div>
        </aside>

        {/* Preview area */}
        <div className="min-w-0" data-testid="capability-preview-wrap">
          <div className="no-print flex items-center gap-2 mb-3 text-[11.5px] font-bold uppercase tracking-wider text-slate-500">
            <Eye className="w-3.5 h-3.5" strokeWidth={2} /> Live preview · US Letter (8.5 × 11 in)
          </div>
          <div className="cap-preview-scaler">
            <CapabilityDocument
              recipient={recipient}
              vehicles={vehicles}
              naics={naics}
              note={note}
              generatedAt={encoded.issuedAt}
              integrity={encoded.integrity}
              qrDataUrl={qrDataUrl}
              verifyUrl={encoded.verifyUrl}
            />
          </div>
          <div className="no-print mt-5 rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap gap-3 items-center justify-between" data-testid="print-hint">
            <div className="text-[12px] text-slate-600">
              <span className="font-bold text-slate-900">Tip:</span> In your browser's print dialog choose <span className="font-semibold">Save as PDF</span> · paper size <span className="font-semibold">Letter</span> · margins <span className="font-semibold">None</span> · <span className="font-semibold">Background graphics on</span>.
            </div>
            <button onClick={printDoc} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-red text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-red-deep" data-testid="capability-print-bottom">
              <Download className="w-4 h-4" strokeWidth={1.8} /> Save as PDF
            </button>
          </div>
        </div>
      </section>

      {/* How verification works */}
      <section className="no-print bg-white border-t border-slate-200" data-testid="capability-verify-explain">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-3 gap-5">
          <motion.div initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true}} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="w-10 h-10 rounded-lg bg-trital-navy text-white flex items-center justify-center"><FileText className="w-5 h-5" strokeWidth={1.8}/></div>
            <div className="mt-4 font-display font-bold text-[16px] text-slate-900">1 · Personalize</div>
            <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">Type the recipient hospital, select NAICS codes and contract vehicles. The preview updates instantly.</p>
          </motion.div>
          <motion.div initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true,delay:0.05}} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="w-10 h-10 rounded-lg bg-trital-navy text-white flex items-center justify-center"><QrCode className="w-5 h-5" strokeWidth={1.8}/></div>
            <div className="mt-4 font-display font-bold text-[16px] text-slate-900">2 · Send &amp; print</div>
            <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">Copy the shareable link, email it, or print / Save-as-PDF. The QR code in the footer opens the live verification page.</p>
          </motion.div>
          <motion.div initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true,delay:0.1}} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="w-10 h-10 rounded-lg bg-trital-navy text-white flex items-center justify-center"><ShieldCheck className="w-5 h-5" strokeWidth={1.8}/></div>
            <div className="mt-4 font-display font-bold text-[16px] text-slate-900">3 · Verify</div>
            <p className="mt-1.5 text-[13px] text-slate-600 leading-relaxed">Recipients scan the QR (or click the verify URL) and see a timestamped snapshot with the integrity hash — no login required.</p>
          </motion.div>
        </div>
      </section>

      {/* Trust row */}
      <section className="no-print bg-trital-navy-ink text-white" data-testid="capability-trust">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-trital-red" strokeWidth={1.8} />
            <span className="text-[13px] font-semibold">TRITAL Care · Healthcare-only workforce</span>
          </div>
          <div className="text-[12px] text-white/70">NMSDC · HMSDC · NCTRCA · SAM.gov</div>
          <div className="ml-auto flex items-center gap-2">
            <Link to="/contact" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-trital-navy text-[12.5px] font-semibold hover:bg-slate-100">
              Talk to procurement
            </Link>
          </div>
        </div>
      </section>

      <div className="no-print"><PublicFooter /></div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page { size: Letter; margin: 0; }
          html, body, #root, .App { background: #fff !important; }
          .no-print { display: none !important; }
          .cap-preview-scaler { transform: none !important; width: auto !important; }
          .cap-doc { box-shadow: none !important; border: 0 !important; border-radius: 0 !important; page-break-inside: avoid; }
          [data-testid="capability-statement-page"] > section,
          [data-testid="capability-statement-page"] > div { padding: 0 !important; margin: 0 !important; max-width: none !important; }
          [data-testid="capability-grid"] { display: block !important; padding: 0 !important; }
          [data-testid="capability-preview-wrap"] { padding: 0 !important; margin: 0 !important; }
        }
        /* Make the preview responsive on narrow screens without breaking the 8.5in layout */
        @media (max-width: 1024px) {
          .cap-preview-scaler { overflow-x: auto; padding-bottom: 0.5rem; }
          .cap-preview-scaler .cap-doc { width: 8.5in; max-width: none; }
        }
      `}</style>
    </div>
  );
}

function Field({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
